
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { User } from '@supabase/supabase-js';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSuccess: () => void;
}

const countries = [
  { code: '+1', name: 'United States', flag: '🇺🇸' },
  { code: '+91', name: 'India', flag: '🇮🇳' },
  { code: '+44', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+86', name: 'China', flag: '🇨🇳' },
  { code: '+81', name: 'Japan', flag: '🇯🇵' },
  { code: '+49', name: 'Germany', flag: '🇩🇪' },
  { code: '+33', name: 'France', flag: '🇫🇷' },
  { code: '+61', name: 'Australia', flag: '🇦🇺' },
  { code: '+55', name: 'Brazil', flag: '🇧🇷' },
  { code: '+7', name: 'Russia', flag: '🇷🇺' },
  { code: '+82', name: 'South Korea', flag: '🇰🇷' },
  { code: '+39', name: 'Italy', flag: '🇮🇹' },
  { code: '+34', name: 'Spain', flag: '🇪🇸' },
  { code: '+52', name: 'Mexico', flag: '🇲🇽' },
  { code: '+31', name: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', name: 'Sweden', flag: '🇸🇪' },
  { code: '+47', name: 'Norway', flag: '🇳🇴' },
  { code: '+41', name: 'Switzerland', flag: '🇨🇭' },
  { code: '+65', name: 'Singapore', flag: '🇸🇬' },
  { code: '+971', name: 'UAE', flag: '🇦🇪' },
];

export const EditProfileModal = ({ isOpen, onClose, user, onSuccess }: EditProfileModalProps) => {
  const [email, setEmail] = useState(user.email || '');
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [emailConsent, setEmailConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const { toast } = useToast();

  // Load current profile data when modal opens
  React.useEffect(() => {
    if (isOpen && user) {
      loadProfileData();
    }
  }, [isOpen, user]);

  const loadProfileData = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('phone_number, country_code, full_name')
        .eq('id', user.id)
        .single();

      if (profile) {
        setPhoneNumber(profile.phone_number || '');
        setCountryCode(profile.country_code || '+1');
        setFullName(profile.full_name || '');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^\d{6,15}$/;
    if (!phone) {
      return 'Phone number is required';
    }
    if (!phoneRegex.test(phone)) {
      return 'Please enter a valid phone number (6-15 digits)';
    }
    return '';
  };

  const validateFullName = (name: string) => {
    if (!name || name.trim().length < 2) {
      return 'Full name must be at least 2 characters';
    }
    return '';
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError(validateEmail(value));
    // Reset consent when email changes
    if (value !== user.email) {
      setEmailConsent(false);
    }
  };

  const handlePhoneChange = (value: string) => {
    // Remove non-digit characters
    const cleanedValue = value.replace(/\D/g, '');
    setPhoneNumber(cleanedValue);
    setPhoneError(validatePhone(cleanedValue));
  };

  const handleFullNameChange = (value: string) => {
    setFullName(value);
    setFullNameError(validateFullName(value));
  };

  const handleCountryChange = (value: string) => {
    const country = countries.find(c => c.code === value);
    if (country) {
      setCountryCode(country.code);
    }
  };

  const checkEmailExists = async (emailToCheck: string) => {
    try {
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', emailToCheck)
        .neq('id', user.id)
        .maybeSingle();

      return !!existingProfile;
    } catch (error) {
      console.error('Error checking email existence:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const emailValidation = validateEmail(email);
    const phoneValidation = validatePhone(phoneNumber);
    const fullNameValidation = validateFullName(fullName);
    
    setEmailError(emailValidation);
    setPhoneError(phoneValidation);
    setFullNameError(fullNameValidation);
    
    if (emailValidation || phoneValidation || fullNameValidation) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors below",
        variant: "destructive",
      });
      return;
    }

    // Check if email is being changed
    const emailChanged = email !== user.email;
    
    // If email is changed, check consent
    if (emailChanged && !emailConsent) {
      toast({
        title: "Email Consent Required",
        description: "Please confirm that you want to change your email and agree to receive the Secure Login link",
        variant: "destructive",
      });
      return;
    }

    // Check for duplicate email if email is being changed
    if (emailChanged) {
      const emailExists = await checkEmailExists(email);
      if (emailExists) {
        setEmailError('This email is already registered');
        toast({
          title: "Email Already Exists",
          description: "This email is already registered. Please use a different email.",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Update name and phone number instantly
      const selectedCountry = countries.find(c => c.code === countryCode);
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          phone_number: phoneNumber,
          country_code: countryCode,
          country_name: selectedCountry?.name || 'United States',
        })
        .eq('id', user.id);

      if (profileError) {
        throw new Error(`Profile update failed: ${profileError.message}`);
      }

      // Handle email update if changed
      if (emailChanged) {
        const redirectUrl = `${window.location.origin}/`;
      
        const { error: emailError } = await supabase.auth.updateUser(
          { email: email },
          { emailRedirectTo: redirectUrl }
        );
      
        if (emailError) {
          throw new Error(`Email update failed: ${emailError.message}`);
        }
      
        // ✅ NEW STEP: update email_updated = true in 'profiles' table
        const { error: emailUpdatedError } = await supabase
          .from("profiles")
          .update({ email_updated: true })
          .eq("id", user.id);
      
        if (emailUpdatedError) {
          console.error("Failed to update email_updated flag:", emailUpdatedError);
        }
      
        toast({
          title: "Email Change Initiated",
          description:
            "Please check your new email for a confirmation link. You can continue using the app with your current email until you confirm the new one.",
        });
                  
        // Don't sign out - let user continue until they confirm new email
      } else {
        toast({
          title: "Profile Updated",
          description: "Your profile has been updated successfully!",
        });
      }
      
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const emailChanged = email !== user.email;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-[#90fbff] via-[#fff6ff] to-[#ffb1d7] shadow-xl border border-pink-200/50 backdrop-blur-md rounded-xl">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update your profile information
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => handleFullNameChange(e.target.value)}
              placeholder="Enter your full name"
            />
            {fullNameError && (
              <p className="text-sm text-red-500">{fullNameError}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="Enter your email"
            />
            {emailError && (
              <p className="text-sm text-red-500">{emailError}</p>
            )}
          </div>

          {emailChanged && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="emailConsent"
                  checked={emailConsent}
                  onCheckedChange={(checked) => setEmailConsent(checked as boolean)}
                />
                <label
                  htmlFor="emailConsent"
                  className="text-sm text-gray-700 cursor-pointer"
                >
                  I confirm that the new email address is correct and I agree to receive a secure link to verify this email change.
                </label>
              </div>
              {emailChanged && !emailConsent && (
                <p className="text-sm text-amber-600">
                  ⚠️ Email verification required: A secure link will be sent to your new email address.
                </p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Select value={countryCode} onValueChange={handleCountryChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    <div className="flex items-center gap-2">
                      <span>{country.flag}</span>
                      <span>{country.name}</span>
                      <span className="text-gray-500">({country.code})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <div className="flex gap-2">
              <div className="flex items-center px-3 py-2 border rounded-md bg-gray-50 text-sm min-w-fit">
                {countryCode}
              </div>
              <Input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="Enter phone number"
                maxLength={15}
              />
            </div>
            {phoneError && (
              <p className="text-sm text-red-500">{phoneError}</p>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? "Updating..." : "Update Profile"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
